// Revenue Budget — the billable revenue a job is bid to earn, broken out by
// cost code x cost type on the same lines as its Day Zero cost budget.
//
// Fusion's cost budget carries revenue as a single 4xxxx line (990000 / 41000).
// A cost-plus VoltaGrid job bills every section at its own markup (labor at
// the rate sheet, third party at cost + 12%, a fee on the project team), so
// the Overview needs the revenue each section was bid at, not one lump spread
// by cost. It lives on its own 'Revenue Budget' tab per forecast version, in
// the Day Zero Budget's row shape (field 'revenueRow', rowId 0..N), and is
// never pushed to JDE.

import { pNum } from '../db/company';

export const REVENUE_BUDGET_TAB = 'Revenue Budget';
export const REVENUE_BUDGET_FIELD = 'revenueRow';

// The Day Zero import's columns. 'Amount' is the revenue; 'Cost Budget' is a
// reference column the template carries and the import ignores.
export const REVENUE_COLUMNS = ['Cost Code', 'Cost Code Description', 'Cost Type', 'Cost Type Description', 'Amount', 'Hours'];
export const REVENUE_REQUIRED = ['Cost Code', 'Cost Type', 'Amount'];

const str = (v) => (v === null || v === undefined ? '' : String(v).trim());

// A line saved from the Day Zero import's Revenue column carries the Day Zero
// row it was typed on in Row_Label, so the cost grid shows it on that row.
const DAY_ZERO_LABEL = 'dayZero:';
export const dayZeroRowLabel = (rowId) => `${DAY_ZERO_LABEL}${rowId}`;
const dayZeroRowOf = (label) => (str(label).startsWith(DAY_ZERO_LABEL) ? str(label).slice(DAY_ZERO_LABEL.length) : null);

// The Day Zero import's optional Billable Cost column (the line's revenue),
// first header found wins; 'Revenue' is the name it first shipped under.
export const DAY_ZERO_REVENUE_HEADERS = ['Billable Cost', 'Revenue', 'Billable Revenue', 'Billable'];

// Latest save wins per (tab, field, row) — the Overview's dedupe.
const stampOf = (d) => Math.max(
    new Date(d.DSS_Created_Date || d.DSS_Created_Datetime || 0).getTime() || 0,
    new Date(d.DSS_Updated_Datetime || 0).getTime() || 0,
);

// The version's revenue budget lines: [{ cc, ct, amount, hours, rowId, guid }].
// A line with no cost code or no amount is not a line.
export function revenueBudgetRows(userData, version) {
    const latest = {};
    (Array.isArray(userData) ? userData : []).forEach((d) => {
        if (!d || d.Forecast_Tab !== REVENUE_BUDGET_TAB) return;
        if (version != null && String(d.Version_GUID) !== String(version)) return;
        const key = `${d.BU_ID}_${d.Forecast_Field_Name}_${d.Forecast_Row_ID}`;
        const at = stampOf(d);
        if (!latest[key] || at >= latest[key].at) latest[key] = { at, d };
    });
    return Object.values(latest)
        .map(({ d }) => ({
            cc: str(d.Cost_Code),
            ct: str(d.Cost_Type),
            amount: pNum(d.Value_Amount || 0),
            hours: pNum(d.Hours || 0),
            rowId: str(d.Forecast_Row_ID),
            dzRow: dayZeroRowOf(d.Row_Label),
            guid: d.Forecast_User_Data_GUID == null ? null : d.Forecast_User_Data_GUID,
        }))
        .filter((r) => r.cc && r.amount)
        .sort((a, b) => (Number(a.rowId) - Number(b.rowId)) || a.rowId.localeCompare(b.rowId));
}

// Revenue by cost code (and by code x cost type), plus the total. `has` is
// false when the version carries no revenue budget, so callers fall back to
// pricing the cost budget.
export function revenueBudgetByCode(userData, version) {
    const byCode = {};
    const byCodeType = {};
    let total = 0;
    const lines = revenueBudgetRows(userData, version);
    lines.forEach((r) => {
        byCode[r.cc] = (byCode[r.cc] || 0) + r.amount;
        const t = byCodeType[r.cc] || (byCodeType[r.cc] = {});
        t[r.ct] = (t[r.ct] || 0) + r.amount;
        total += r.amount;
    });
    return { has: lines.length > 0, byCode, byCodeType, total, lines };
}

// An uploaded sheet (sheet_to_json with header: 1) -> revenue lines. The
// first sheet whose header row has the required columns wins, as on the Day
// Zero import. Revenue (4xxxx) lines and 990000 are skipped: the revenue
// budget is the breakout of that lump, so a Day Zero file dropped in here as
// is must not count its revenue line twice.
export function parseRevenueSheets(sheets) {
    const list = Array.isArray(sheets) ? sheets : [];
    const hasAll = (hdr) => REVENUE_REQUIRED.every((c) => (hdr || []).map(str).includes(c));
    const aoa = list.find((s) => Array.isArray(s) && hasAll(s[0]));
    if (!aoa) return { rows: [], skipped: [], error: `No sheet has the columns ${REVENUE_REQUIRED.join(', ')}.` };
    const hdr = aoa[0].map(str);
    const col = (name) => hdr.indexOf(name);
    const rows = [];
    const skipped = [];
    aoa.slice(1).forEach((r, i) => {
        if (!Array.isArray(r)) return;
        const cc = str(r[col('Cost Code')]);
        const ct = str(r[col('Cost Type')]);
        const amount = pNum(r[col('Amount')] || 0);
        if (!cc && !ct && !amount) return;
        const line = {
            cc, ct, amount,
            hours: col('Hours') >= 0 ? pNum(r[col('Hours')] || 0) : 0,
            desc: col('Cost Code Description') >= 0 ? str(r[col('Cost Code Description')]) : '',
            sheetRow: i + 2,
        };
        if (!cc || !ct) skipped.push({ ...line, why: 'no cost code / cost type' });
        else if (cc === '990000' || ct.startsWith('4')) skipped.push({ ...line, why: 'revenue line (the breakout replaces it)' });
        else if (!amount) skipped.push({ ...line, why: 'no amount' });
        else rows.push(line);
    });
    return { rows, skipped, error: null };
}

// What an upload writes: every line upserted on rowId 0..N (the Day Zero's
// keying), and the version's older lines past N deleted by their GUID so a
// shorter upload doesn't leave a tail behind.
export function revenueBudgetSaves({ rows, existing, job, version, lockedVersion, userId, userName }) {
    const saves = (rows || []).map((r, i) => ({
        userId: userId || '',
        userName: userName || '',
        fieldName: REVENUE_BUDGET_FIELD,
        tab: REVENUE_BUDGET_TAB,
        rowId: String(i),
        version: String(version),
        id: job,
        lockedVersion,
        value: String(r.amount),
        costCode: r.cc,
        costType: r.ct,
        valueAmount: String(r.amount),
        hours: r.hours ? String(r.hours) : '',
        description: r.desc || '',
        rowLabel: r.rowLabel || '',
    }));
    const kept = new Set(saves.map((s) => s.rowId));
    const deletes = (existing || [])
        .filter((d) => d && d.Forecast_Tab === REVENUE_BUDGET_TAB && String(d.Version_GUID) === String(version)
            && d.Forecast_User_Data_GUID != null
            && !(d.Forecast_Field_Name === REVENUE_BUDGET_FIELD && kept.has(str(d.Forecast_Row_ID))))
        .map((d) => ({ id: job, forecastGuid: version, rowId: d.Forecast_User_Data_GUID, tab: REVENUE_BUDGET_TAB }));
    return { saves, deletes };
}

// The Day Zero import's Revenue column -> Revenue Budget lines: one per
// saved Day Zero row with revenue (`savedRows[i]` is saved as rowId i),
// labelled with that row. The 4xxxx / 990000 lump is the total the breakout
// ties to, not a line.
export function dayZeroRevenueLines(savedRows, descOf) {
    const lines = [];
    (Array.isArray(savedRows) ? savedRows : []).forEach((r, i) => {
        const amount = pNum((r && r.Revenue) || 0);
        const cc = str(r && r.Cost_Code);
        const ct = str(r && r.Cost_Type);
        if (!amount || !cc || !ct || cc === '990000' || ct.startsWith('4')) return;
        lines.push({
            cc, ct, amount,
            hours: pNum(r.Hours || 0),
            desc: (typeof descOf === 'function' && descOf(cc)) || '',
            rowLabel: dayZeroRowLabel(i),
        });
    });
    return lines;
}

// The Day Zero cost grid's Revenue column: each grid row's revenue off the
// version's Revenue Budget, aligned to `dayZeroRows` by index. A line from
// the Day Zero import sits on the row it was typed on (while that row still
// has its code x type). Any other line is per code x type; where the cost
// budget spreads a code x type over several lines, it splits by their cost,
// evenly when they carry none. Unsaved (notFound) rows get nothing. Revenue
// with no cost line to sit on comes back as `unmatched` so the grid can say
// so rather than drop it.
export function dayZeroBillable({ dayZeroRows, userData, version }) {
    const lines = revenueBudgetRows(userData, version);
    const list = Array.isArray(dayZeroRows) ? dayZeroRows : [];
    const amounts = list.map(() => null);
    const unmatched = [];
    if (!lines.length) return { has: false, amounts, total: 0, unmatched };
    const keyOf = (cc, ct) => `${cc}|${ct}`;
    const groups = {};
    const byRowId = {};
    list.forEach((d, i) => {
        if (!d || d.notFound) return;
        const k = keyOf(str(d.Cost_Code), str(d.Cost_Type));
        (groups[k] || (groups[k] = [])).push(i);
        if (d.Forecast_Row_ID != null) byRowId[str(d.Forecast_Row_ID)] = i;
    });
    const add = (i, amt) => { amounts[i] = (amounts[i] || 0) + amt; };
    const pooled = {};
    const pinned = new Set();
    lines.forEach((l) => {
        const k = keyOf(l.cc, l.ct);
        const i = l.dzRow == null ? undefined : byRowId[l.dzRow];
        if (i !== undefined && keyOf(str(list[i].Cost_Code), str(list[i].Cost_Type)) === k) { add(i, l.amount); pinned.add(i); return; }
        pooled[k] = (pooled[k] || 0) + l.amount;
    });
    Object.entries(pooled).forEach(([k, amount]) => {
        const all = groups[k];
        if (!all) { const [cc, ct] = k.split('|'); unmatched.push({ cc, ct, amount }); return; }
        const open = all.filter((i) => !pinned.has(i));
        const idx = open.length ? open : all;
        const costs = idx.map((i) => Math.abs(pNum(list[i].Value_Amount || 0)));
        const sum = costs.reduce((a, b) => a + b, 0);
        idx.forEach((i, j) => add(i, sum ? amount * costs[j] / sum : amount / idx.length));
    });
    unmatched.sort((a, b) => a.cc.localeCompare(b.cc) || a.ct.localeCompare(b.ct));
    return { has: true, amounts, total: lines.reduce((a, l) => a + l.amount, 0), unmatched };
}

// The Revenue Budget screen's roll-up: each revenue line beside the Day Zero
// cost on the same cost code x cost type, grouped by section (`sectionOf(cc)`
// -> { name, order }, e.g. the VoltaGrid WBS). A cost line with no revenue is
// listed too, so an unpriced line shows rather than hides. `dayZeroRevenue`
// is the Day Zero's own 4xxxx lump, which the breakout should tie to.
export function revenueBudgetModel({ userData, version, sectionOf }) {
    const lines = {};
    const lineOf = (cc, ct) => lines[`${cc}|${ct}`] || (lines[`${cc}|${ct}`] = { cc, ct, revenue: 0, cost: 0, hours: 0 });
    revenueBudgetRows(userData, version).forEach((r) => {
        const l = lineOf(r.cc, r.ct);
        l.revenue += r.amount;
        l.hours += r.hours;
    });
    let dayZeroRevenue = 0;
    const latest = {};
    (Array.isArray(userData) ? userData : []).forEach((d) => {
        if (!d || d.Forecast_Tab !== 'Day Zero Budget' || String(d.Version_GUID) !== String(version)) return;
        const key = `${d.BU_ID}_${d.Forecast_Field_Name}_${d.Forecast_Row_ID}`;
        const at = stampOf(d);
        if (!latest[key] || at >= latest[key].at) latest[key] = { at, d };
    });
    Object.values(latest).forEach(({ d }) => {
        const cc = str(d.Cost_Code);
        const ct = str(d.Cost_Type);
        const amt = pNum(d.Value_Amount || 0);
        if (ct.startsWith('4')) { dayZeroRevenue += amt; return; }
        if (!cc || cc === '990000' || !ct.startsWith('5') || !amt) return;
        lineOf(cc, ct).cost += amt;
    });
    const sections = {};
    Object.values(lines).forEach((l) => {
        const s = (typeof sectionOf === 'function' && sectionOf(l.cc)) || { name: 'Unassigned', order: 999 };
        const sec = sections[s.name] || (sections[s.name] = { name: s.name, order: s.order == null ? 999 : s.order, revenue: 0, cost: 0, lines: [] });
        sec.revenue += l.revenue;
        sec.cost += l.cost;
        sec.lines.push(l);
    });
    const list = Object.values(sections).sort((a, b) => (a.order - b.order) || a.name.localeCompare(b.name));
    list.forEach((s) => s.lines.sort((a, b) => a.cc.localeCompare(b.cc) || a.ct.localeCompare(b.ct)));
    const revenue = list.reduce((a, s) => a + s.revenue, 0);
    const cost = list.reduce((a, s) => a + s.cost, 0);
    return { sections: list, revenue, cost, dayZeroRevenue, has: revenue !== 0 };
}
